import type { ReactNode } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  PERSONA_INITIALS,
  PERSONA_LABELS,
  type Audit,
  type GuardianType,
} from "@/services/api/guardiansv2/types";
import { Badge } from "@/components/ui/badge/Badge";
import { RATING_BADGE_COLOR } from "../shared/constants";
import { personasFrom } from "./auditNavigation";
import type { PersonaRerunAction } from "./AuditPersonaRerunActions";

const GUARDIANS: GuardianType[] = ["ace", "ivy", "nova"];

export type AuditPersonaTabId = GuardianType | "synthesis";

export type PersonaTabVisualState = "default" | "running";

export interface PersonaTabProgressContext {
  auditRerunning: boolean;
  personaRerunAction: PersonaRerunAction | null;
  rerunAnchorTab: AuditPersonaTabId | null;
}

export interface AuditPersonaTabsProps {
  audit: Audit;
  activeId: AuditPersonaTabId;
  onChange: (id: AuditPersonaTabId) => void;
  progress?: PersonaTabProgressContext;
}

function personaTabLabel(
  audit: Audit,
  persona: GuardianType,
  compact: boolean
): ReactNode {
  const result = audit.personas[persona];
  const name = compact ? PERSONA_INITIALS[persona] : PERSONA_LABELS[persona];
  if (!result) {
    return name;
  }
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <span className="truncate">{name}</span>
      <Badge
        color={RATING_BADGE_COLOR[result.rating]}
        size="sm"
        className="shrink-0 tabular-nums text-[10px]"
      >
        {result.score}/5
      </Badge>
    </span>
  );
}

/** Whether a tab should show the in-progress rerun styling. */
export function computeTabVisualState(
  audit: Audit,
  tabId: AuditPersonaTabId,
  ctx: PersonaTabProgressContext
): PersonaTabVisualState {
  if (ctx.personaRerunAction === "synthesis" && tabId === "synthesis") {
    return "running";
  }
  if (ctx.personaRerunAction === "full") {
    if (tabId === "synthesis") {
      const allGuardiansDone = GUARDIANS.every((p) => audit.personas_completed.includes(p));
      return allGuardiansDone ? "running" : "default";
    }
    const completed = new Set(audit.personas_completed);
    if (completed.has(tabId as GuardianType)) {
      return "default";
    }
    const next = GUARDIANS.find((p) => !completed.has(p));
    return next === tabId ? "running" : "default";
  }
  if (ctx.personaRerunAction === "single" && tabId === ctx.rerunAnchorTab) {
    return "running";
  }
  if (
    ctx.personaRerunAction === "cascade" &&
    ctx.rerunAnchorTab &&
    ctx.rerunAnchorTab !== "synthesis" &&
    tabId !== "synthesis"
  ) {
    const cascade = personasFrom(ctx.rerunAnchorTab as GuardianType);
    if (cascade.includes(tabId as GuardianType)) {
      return "running";
    }
  }

  const inFlight = audit.status === "queued" || audit.status === "running";
  if (!inFlight && !ctx.auditRerunning) {
    return "default";
  }

  if (tabId === "synthesis") {
    const allGuardiansDone = GUARDIANS.every((p) => audit.personas_completed.includes(p));
    return allGuardiansDone ? "running" : "default";
  }

  const completed = new Set(audit.personas_completed);
  if (completed.has(tabId as GuardianType)) {
    return "default";
  }
  const next = GUARDIANS.find((p) => !completed.has(p));
  return next === tabId ? "running" : "default";
}

/** Synthesis + persona tabs with responsive labels and rerun progress styling. */
export function AuditPersonaTabs({
  audit,
  activeId,
  onChange,
  progress,
}: AuditPersonaTabsProps) {
  const ctx: PersonaTabProgressContext = progress ?? {
    auditRerunning: false,
    personaRerunAction: null,
    rerunAnchorTab: null,
  };

  const tabs: Array<{
    id: AuditPersonaTabId;
    label: (compact: boolean) => ReactNode;
  }> = [
    {
      id: "synthesis",
      label: () => "Synthesis",
    },
    ...GUARDIANS.map((persona) => ({
      id: persona,
      label: (compact: boolean) => personaTabLabel(audit, persona, compact),
    })),
  ];

  return (
    <div
      role="tablist"
      className="flex min-w-0 items-stretch gap-0.5 overflow-x-auto border-b border-slate-800/80"
      data-testid="audit-persona-tabs"
    >
      {tabs.map((tab) => {
        const active = tab.id === activeId;
        const visualState = computeTabVisualState(audit, tab.id, ctx);
        const running = visualState === "running";
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            data-testid={`audit-persona-tab-${tab.id}`}
            data-tab-state={visualState}
            className={clsx(
              "relative min-w-0 shrink cursor-pointer px-2 py-2 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/40 sm:px-3 sm:text-sm",
              active && "text-white",
              !active && !running && "text-slate-400 hover:text-slate-200",
              running && "bg-indigo-500/10 text-indigo-200"
            )}
          >
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <span className="sm:hidden">{tab.label(true)}</span>
              <span className="hidden min-w-0 sm:inline">{tab.label(false)}</span>
              {running ? (
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-3 w-3 shrink-0 animate-spin text-indigo-300"
                  aria-hidden
                />
              ) : null}
            </span>
            {active ? (
              <span className="absolute left-1 right-1 -bottom-px h-[2px] rounded-full bg-indigo-300 sm:left-2 sm:right-2" />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Default tab when opening an audit — synthesis first. */
export function defaultPersonaTab(_audit: Audit): AuditPersonaTabId {
  return "synthesis";
}
